import React from 'react';

/**
 * https://reactjs.org/docs/context.html
 * React context global store.
 */
class HelperGlobalDataStore {

	/**
	 * Глобална променлива, използвана за debug.
	 *
	 */
	dataDebug = {};

	constructor() {
		/**
		 * Mimics private property.
		 * Данните (JSON обекти) трябва да се задават през setter и да не са публично достъпни.
		 * Не трябва да се update-ва inner данните директно.
		 */
		let p_data = {};

		/**
		 * Mimics private property.
		 * Обект с references към React компонентите.
		 */
		let p_objs = {};

		/**
		 * When enabled, every mutation (set/init/forceUpdate/bindObj) is logged
		 * to the console. Toggle it via SHDataStore.trace(true|false).
		 */
		let traceEnabled = false;

		const trace = (action, key, extra) => {
			if (process.env.NODE_ENV != 'development' || !traceEnabled) {
				return;
			}
			console.log(
			  `%c[store] ${action}%c ${key ?? ''}`,
			  'color:#8e44ad;font-weight:bold',
			  'color:inherit',
			  extra !== undefined ? extra : ''
			);
		};

		this.forceUpdate = (key) => {
			if (!key) {
				throw '[bindObj] key: property not set';
			}
			if (typeof key !== 'string') {
				throw 'key: expected string';
			}
			if (p_objs[key].length) {
				trace('forceUpdate', key, `→ ${p_objs[key].length} component(s)`);
				p_objs[key].map((item) => {
					item.forceUpdate();
				})
			}
		}

		this.bindObj = (key, object) => {
			if (!key) {
				throw '[bindObj] key: property not set';
			}
			if (typeof key !== 'string') {
				throw 'key: expected string';
			}
			if (object == null) {
				p_objs[key] = []; // unbind
				trace('unbind', key);
			} else {
				if (p_objs[key]) {
					p_objs[key].push(object)
				} else {
					p_objs[key] = [object];
				}
				trace('bind', key, `(now ${p_objs[key].length})`);
			}
		}

		/**
		 * Getter.
		 * @return: Data by Key (if key is set) or all data (if not set).
		 */
		this.get = (key = null) => {
			if (key) {
				return p_data[key];
			} else {
				return p_data;
			}
		}
		/**
		 * Getter.
		 * @return: Data by Key (if key is set) or all data (if not set).
		 */
		this.obj = (key = null) => {
			return p_objs[key];
		}

		/**
		 * Setter (entire dataset)
		 */
		this.init = (data) => {
			p_data = {...p_data, ...data};
			trace('init', `(${Object.keys(data || {}).length} keys)`, data);
			/**
			 * Само за dev mode, за debug.
			 */
			if (process.env.NODE_ENV == 'development') {
				this.dataDebug = {...p_data};
			}
		}

		/**
		 * Setter (key/value)
		 */
		this.set = (key, data) => {
			if (!key) {
				throw '[set] key: property not set';
			}
			if (typeof key !== 'string') {
				throw 'key: expected string';
			}
			p_data[key] = data;
			trace('set', key, data);

			/**
			 * Само за dev mode, за debug.
			 */
			if (process.env.NODE_ENV == 'development') {
				this.dataDebug = {...p_data};
			}
		}

		/**
		 * Debug-only API (trace + dump). Guarded by NODE_ENV so both the method
		 * definitions and their bodies are stripped from staging/prod builds
		 * (DefinePlugin + Terser dead-code elimination), leaving no way to expose
		 * the internal state on live.
		 */
		if (process.env.NODE_ENV == 'development') {

			/**
			 * Enable/disable live console tracing of every store mutation
			 * (set / init / forceUpdate / bind / unbind).
			 * Usage in the browser console: SHDataStore.trace(true)
			 */
			this.trace = (on = true) => {
				traceEnabled = !!on;
				console.log(`%c[store] tracing ${traceEnabled ? 'ON' : 'OFF'}`, 'color:#8e44ad;font-weight:bold');
				return traceEnabled;
			};

			/**
			 * Debug helper. Logs the whole store tree to the console:
			 * every registered key, the components bound to it (from p_objs),
			 * their LIVE per-instance state (core.state) and the written-through
			 * cache (p_data).
			 * Note: initial state lives on each bound instance (core.state); p_data
			 * is only populated after setState/init, so it can legitimately be empty.
			 * Call it from the browser console: SHDataStore.dump()
			 */
			this.dump = () => {
				const keys = Array.from(new Set([...Object.keys(p_objs), ...Object.keys(p_data)])).sort();

				console.group('%cHelperGlobalDataStore', 'color:#e67e22;font-weight:bold', `(${keys.length} keys)`);

				keys.forEach((key) => {
					const objs = Array.isArray(p_objs[key]) ? p_objs[key] : [];
					const mounted = objs.filter((o) => o && o.mounted).length;
					const liveState = objs.length ? objs[objs.length - 1]?.state : undefined;

					console.groupCollapsed(
					  `%c${key}`,
					  'color:#2980b9;font-weight:bold',
					  `— bound: ${objs.length} (mounted: ${mounted})`
					);

					console.log('live state (core.state):', liveState);
					console.log('cached state (p_data):', p_data[key]);

					if (objs.length) {
						console.table(objs.map((o, i) => ({
							index: i,
							componentName: o?.componentName ?? '(n/a)',
							componentToken: o?.componentToken ?? '(n/a)',
							mounted: !!o?.mounted,
							loading: !!o?.loading,
						})));
						console.log('bound instances:', objs);
					} else {
						console.log('%cno bound components (data only)', 'color:#999');
					}

					console.groupEnd();
				});

				console.groupEnd();

				const liveData = {};
				Object.keys(p_objs).forEach((key) => {
					const objs = p_objs[key];
					if (Array.isArray(objs) && objs.length) {
						liveData[key] = objs[objs.length - 1]?.state;
					}
				});

				return {liveData, cachedData: {...p_data}, objs: {...p_objs}};
			};

		}
	}
}

const globalDataStore = new HelperGlobalDataStore();

/**
 * Expose the singleton store on window for debugging (dev only).
 * Usage in the browser console: SHDataStore.dump()
 */
if (typeof window !== 'undefined' && process.env.NODE_ENV == 'development') {
	window.SHDataStore = globalDataStore;
}

export const ContextDataStore = React.createContext(globalDataStore);

